import React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  allocationStatusClass,
  allocationStatusLabel,
} from "../utils/allocationStatus";

function formatQty(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString() : "—";
}

function formatMoney(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatActionType(actionType, t) {
  const normalized = String(actionType || "").toUpperCase();
  if (normalized === "REVOKE") return t("inventory.actionTypeRevoke");
  if (normalized === "UNLOCK") return t("inventory.actionTypeUnlock");
  return actionType || "—";
}

function formatStatus(status, t) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PENDING") return t("inventory.revokeUnlockStatusPending");
  if (normalized === "APPROVED") return t("inventory.revokeUnlockStatusApproved");
  if (normalized === "REJECTED") return t("inventory.revokeUnlockStatusRejected");
  return status || "—";
}

function statusBadgeClass(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "APPROVED") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (normalized === "REJECTED") {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }
  return "bg-amber-50 text-amber-700 border-amber-200";
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   request?: object | null,
 * }} props
 */
export function RevokeUnlockRequestDetailModal({
  open,
  onOpenChange,
  request = null,
}) {
  const { t } = useTranslation();
  const actionUpper = String(request?.actionType || "").toUpperCase();

  const rows = request
    ? [
        {
          label: t("inventory.status"),
          value: (
            <span
              className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border",
                statusBadgeClass(request.status)
              )}
            >
              {formatStatus(request.status, t)}
            </span>
          ),
        },
        {
          label: t("inventory.lineStatus"),
          value:
            request.lineStatus && request.lineStatus !== "—" ? (
              <span
                className={cn(
                  "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold tracking-wider border",
                  allocationStatusClass(request.lineStatus)
                )}
              >
                {allocationStatusLabel(request.lineStatus, t)}
              </span>
            ) : (
              "—"
            ),
        },
        {
          label: t("inventory.actionType"),
          value: formatActionType(request.actionType, t),
        },
        {
          label: t("inventory.customerName"),
          value: request.customerName || "—",
        },
        {
          label: t("inventory.productName"),
          value: request.productName || "—",
        },
        {
          label: t("inventory.partNumber"),
          value: request.partNumber || "—",
        },
        {
          label: t("inventory.batchNumber"),
          value: request.batchNumber || "—",
        },
        {
          label: t("inventory.warehouse"),
          value: request.warehouseName || "—",
        },
        {
          label: t("inventory.pricePerUnit"),
          value: formatMoney(request.purchasePricePerUnit),
        },
        {
          label: t("inventory.allocatedQty"),
          value: formatQty(request.allocatedQuantity),
        },
        ...(actionUpper === "REVOKE"
          ? [
              {
                label: t("inventory.revokeQuantity"),
                value: formatQty(request.revokeQuantity),
              },
            ]
          : []),
        ...(actionUpper === "UNLOCK"
          ? [
              {
                label: t("inventory.unlockedPrice"),
                value: formatMoney(request.unlockedPrice),
              },
            ]
          : []),
        {
          label: t("inventory.iofNumber"),
          value: request.iofNumber || request.reference || "—",
        },
        {
          label: t("inventory.reason"),
          value: request.reason || "—",
        },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[520px] max-h-[85vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden overflow-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <DialogTitle className="text-[16px] font-bold text-foreground">
              {t("inventory.revokeUnlockRequestDetail")}
            </DialogTitle>
            <DialogPrimitive.Close className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden">
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="overflow-y-auto min-h-0 space-y-2.5">
            {rows.map((row) => (
              <div
                key={row.label}
                className="flex items-start justify-between gap-4 rounded-xl border border-(--color-text-border)/40 bg-workspace/20 px-3.5 py-2.5"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 shrink-0">
                  {row.label}
                </span>
                <span className="text-xs font-semibold text-foreground text-right break-words">
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2 border-t border-(--color-text-border)/50 shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-5 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
            >
              {t("formActions.close")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export { formatActionType, formatStatus, statusBadgeClass };
